/**
 * api req & res 와 관련된 타입을 정의합니다. (bff & iam)
 */

import type { ValueOf } from '@kc-console/core';
import type {
  DomainsApiApiV11DomainsDomainIdGetRequest,
  ResponseCloudProject,
  ResponseCloudRole,
  ResponseCloudUser,
  ResponseDomainGetResponse,
  ResponsePrincipalDto,
  ResponseSimpleCloudDomain,
  ResponseSimpleCloudProjectDto,
  UsersApiApiV1UsersUserIdGetRequest,
  UsersApiApiV1UsersUserIdPatchRequest,
  UsersApiApiV11UsersUserIdProjectsGetRequest,
} from '../../api/iam/openapi';
import type { CONSOLE_VERSION } from '../common';
import type {
  Partition,
  Region,
  RouteLayoutType,
  RouteTypeType,
  SkeletonTypeType,
  TokenScope,
} from '.';

type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/**
 * BFF api req & res
 */

export type ConsoleConfig = {
  maintenance: boolean;
  partition: Partition;
};

export type ServicePhase = 'alpha' | 'beta' | '';

interface ServiceMenu {
  /** 서비스 접근 가능 여부 */
  accessible: boolean;
  /** 서비스 설명 */
  description: string;
  /** 클릭시 아웃링크 여부 */
  external: boolean;
  /** gnb 표기 여부 */
  gnb: boolean;
  /** 카테고리 정보인지 아닌지 판단할 수 있는 플래그 */
  isCategory: boolean;
  /** 서비스 메뉴의 maintenance 여부 */
  maintenance: boolean;
  /** 해낭 메뉴로 접근할 수 있는 주소 */
  path: string;
  /** alpha, beta, GA의 서비스 phase */
  phase: ServicePhase;
  /** 해당 서비스의 포털 링크 */
  portalUrl: string;
  /** 해당 메뉴의 scope 정보 */
  scope: TokenScope;
  /** 글로벌 검색창에서 검색 결과로 노출할 지 여부를 설정 */
  searchable: boolean;
  /** 해당 서비스의 유저가이드 링크 */
  userGuide: string;
  /** 해당 서비스의 버전 */
  version: string;
}

export interface MenuData extends Partial<ServiceMenu> {
  /** 해당 서비스가 속한 카테고리. 카테고리의 경우는 빈 값으로 내려옴 */
  category: string;
  /** LNB, GNB에 노출 여부 */
  display: boolean;
  /** 서비스 메뉴의 id, 유니크한 값이어야함 */
  id: string;
  /** lnb에서 사용될 서비스 아이콘 */
  lnbServiceIcon: string;
  /** 메가메뉴에서 사용될 서비스 아이콘 */
  megaServiceIcon: string;
  /** 서비스 메뉴의 표기명. 콘솔에서 노출될 이름 */
  name: string;
  /** 해당 메뉴에서 접근 할 수 있는 하위 메뉴들, 각 prediction은 path로 저장됨 */
  predictions: string[];
  /** 서비스 (페이지) 접근 시 브라우저 상단 표기명. */
  title: string;
}

export interface MenuDataModel {
  /** key는 각 메뉴의 path로 내려옴 */
  [key: string]: MenuData;
}

export interface MegaMenuDataModel {
  /** key는 각 메뉴의 path로 내려옴 */
  [key: string]: MenuData & {
    servicesInfo: { [key: string]: MenuData & { predictionsInfo: MenuDataModel } };
  };
}

export interface GnbMenuDataModel {
  bill: MenuDataModel;
  helpdesk: MenuDataModel;
  iam: MenuDataModel;
  system: MenuDataModel;
}

export interface LnbMenuDataModel {
  domain: MenuDataModel;
  project: MenuDataModel;
  unscope: MenuDataModel;
}

export type RefinedMenus = {
  category: MenuDataModel;
  currentService: MenuData;
  gnb: GnbMenuDataModel;
  lnb: LnbMenuDataModel;
  mega: MegaMenuDataModel;
  origin: MenuDataModel;
};

export type Recent = {
  project: string[];
  region: Region[];
  service: string[];
};

export type Endpoint = {
  [key: string]: string | null;
};

export type Config = {
  currentRegion: Region;
  disablePopups: string[];
  endpoint: Endpoint;
  favorite: FavoriteService;
  menu: MenuDataModel;
  recent: Recent;
};

type ConfigRecord = 'project' | 'service' | 'region';

export enum BadgeType {
  PHASE = 'PHASE',
  STATUS = 'STATUS',
}

export enum BadgeLabel {
  BETA = 'Beta',
  NEW = 'New',
  UPDATE = 'Update',
  ALPHA = 'Alpha',
}

/**
 * 서비스 Badge
 */
export interface ServiceBadge {
  endDate: string;
  id: number;
  name: BadgeLabel;
  startDate: string;
  type: BadgeType;
}

export interface FetchFavoriteReqParams {
  type: ConfigRecord;
}

export type FetchRecentReqParams = FetchFavoriteReqParams;

export interface FetchConfigReqParams {
  region: Region;
  serviceId: string;
}

export type DeleteFavoriteReqParams = PutFavoriteReqParams;

export type FetchEndpointsV2ReqParams = FetchConfigReqParams;

export type FetchMenusV2ReqParams = {
  region: Region;
};

export interface PutFavoriteReqParams {
  targetId: string;
  type: ConfigRecord;
}
export type PutRecentReqParams = PutFavoriteReqParams;

export type FetchUserGuidesReqParams = FetchConfigReqParams;

export interface FetchPopupReqParams {
  popupId: string;
}
export type PostPopupReqParams = FetchPopupReqParams;

export type GlobalNotice = { neverSeeAgain: boolean; noticeUrl: string };

/**
 * IAM api req & res
 */
export enum RoleType {
  BASIC = 'basic',
  STANDARD = 'standard',
  CUSTOM = 'custom',
}

export type AppCloudUser = ResponseCloudUser;

export type UserProject = ResponseCloudProject;

export type AppCloudProject = ResponseSimpleCloudProjectDto;

export type AppCloudDomain = ResponseSimpleCloudDomain;

export type AppCloudRole = ResponseCloudRole;

export type CloudUserInfo = RequiredBy<ResponsePrincipalDto, 'user' | 'roles'> & {
  authToken: string;
};

export type FetchProjectsReqParams = Omit<
  UsersApiApiV11UsersUserIdProjectsGetRequest,
  'xAuthToken'
>;

export interface FetchProjectsResParams {
  projects: UserProject[];
}

export type FetchUserDetailInfoReqParams = Omit<UsersApiApiV1UsersUserIdGetRequest, 'xAuthToken'>;

export type FetchDomainDetailInfoReqParams = Omit<
  DomainsApiApiV11DomainsDomainIdGetRequest,
  'xAuthToken'
>;

export type UpdateUserDetailInfoReqParams = Omit<
  UsersApiApiV1UsersUserIdPatchRequest,
  'xAuthToken'
>;

export type FetchUserInfoResParams = ResponseCloudUser;

export type FetchDomainDetailInfoResParams = ResponseDomainGetResponse;

// ----------MetaApi --------------

export type ServiceMigrationStatus = 'INCOMPLETE' | 'COMPLETED' | 'CUTOVER';
export type ConsoleVersion = ValueOf<typeof CONSOLE_VERSION>;

export type PatchUserSettingsParams = {
  consoleVersion: ConsoleVersion;
};

export type PostUrlValidationParams = {
  path: string;
  serviceKeyword: string;
};

export type FetchPopularServiceParams = {
  projectId?: string;
  region?: Region;
};

export type FetchServiceEndpointParams = {
  region: Region;
  serviceId: string;
};

export type FetchKeywordMetadataParams = {
  serviceKeyword: string;
  region?: Region;
};

export type FetchServiceMenuParams = {
  region: Region;
  serviceId: string;
};

export type FetchMegaMenuParams = {
  /**
   * 'all'은 전 리전 통합 메가메뉴 조회(useAllRegionMegaMenuQuery)를 위한 전용 값입니다.
   * 전 리전 통합 메가메뉴 데이터를 받기 위한 용도로만 사용하며, 그 외 목적으로 임의 사용하지 마세요.
   */
  region?: Region | 'all';
};

export type FetchServiceMetaParams = {
  serviceId: number;
  region?: Region;
};

export type PostRecentServiceParams = {
  serviceKeyword: string;
};

export type PostRecentRegionParams = {
  region: Region;
};

export type PostRecentProjectParams = {
  projectId: string;
};

export type FavoriteServicesParams = {
  serviceKeywords: string[];
};

export type FetchUserPreferenceParams = {
  serviceKeyword: string;
};

export enum ViewPoint {
  DASHBOARD = 'dashboard',
  MEGAMENU = 'megamenu',
  SEARCH = 'search',
}

export interface Menus {
  keyword: string;
  level: number;
  name: string;
  path: string;
  scope: TokenScope;
  subMenus: Menus[];
  attribute?: { isDefaultOpen?: boolean; isSubtitle?: boolean };
}

export interface MegaMenuService {
  badges: ServiceBadge[];
  description: string | null;
  id: number;
  keyword: string;
  menus: Menus[];
  name: string;
  path: string;
  scope: TokenScope;
  viewPoint: ViewPoint;
}

export interface MegaMenuCategory {
  description: string | null;
  id: number;
  keyword: string;
  name: string;
  path: string;
  region: string;
  services: MegaMenuService[];
}

export interface ServiceMeta {
  badges: ServiceBadge[];
  description: string;
  endpoints: Record<string, string>;
  guides: Record<string, string>;
  id: number;
  keyword: string;
  menus: Menus[];
  name: string;
  path: string;
  region: Region;
  routes: ServiceMetaRoute[];
  variables: Record<string, string>;
  version: ServiceMetaVersion;
}

export interface ServiceMetaRoute {
  id: number;
  layoutType: RouteLayoutType;
  moduleId: string;
  path: string;
  routeType: RouteTypeType;
  scope: TokenScope;
  skeletonType: SkeletonTypeType;
  redirectPath?: string;
}

export interface ServiceMetaVersion {
  createdAt: string;
  label: string;
  path: string;
}

export interface FavoriteService {
  keyword: string;
  name: string;
  path: string;
  scope: TokenScope;
}

export interface RecentService {
  keyword: string;
  name: string;
  path: string;
  scope: TokenScope;
}

export interface PopularService {
  id: number;
  keyword: string;
  name: string;
  path: string;
  scope: TokenScope;
}

export interface UserPreference {
  consoleVersion: ConsoleVersion;
  serviceMigrationStatus: ServiceMigrationStatus;
}

// ----------TranslationApi --------------

export type FetchTranslationsParams = {
  keywords: string;
  languages: string;
};

export interface Translation {
  [key: string]: string;
}

export interface TranslationsResponse {
  keyword: string;
  translations: {
    [language: string]: Translation;
  };
}
